import React from 'react';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import { getGeoJsonFromResultSet } from './mapUtils';
import MapLayerControl from './MapLayerControl';
import MapFullscreenControl from './MapFullscreenControl';
import MapSearchOnMoveControl from './MapSearchOnMoveControl';

const propTypes = {
  mapAccessToken: PropTypes.string,
  mapStyle: PropTypes.string,
  mapContainer: PropTypes.string.isRequired,
  mapCenter: PropTypes.arrayOf(PropTypes.number),
  mapZoom: PropTypes.number,
  mapIsScaleControlVisible: PropTypes.bool,
  mapIsNavigationControlVisible: PropTypes.bool,
  mapIsFullscreenControlVisible: PropTypes.bool,
  mapIsMapLayerControlVisible: PropTypes.bool,
  mapIsZoomByScrollEnabled: PropTypes.bool,
  mapIsSearchOnMoveControlVisible: PropTypes.bool,
  mapSelectedFeatureId: PropTypes.string,
  mapGeoJsonData: PropTypes.object,
  mapShouldDisplayIconOnMap: PropTypes.bool,
  onMapClick: PropTypes.func,
  onMapMoveEnd: PropTypes.func,
  onMapSearchOnMoveClick: PropTypes.func,
  locationData: PropTypes.any
};

const defaultProps = {
  mapAccessToken: process.env.MAPBOX_ACCESS_TOKEN,
  mapStyle: process.env.MAPBOX_STYLE,
  mapCenter: [0, 0],
  mapZoom: 4,
  mapIsScaleControlVisible: true,
  mapIsNavigationControlVisible: true,
  mapIsFullscreenControlVisible: true,
  mapIsMapLayerControlVisible: true,
  mapIsZoomByScrollEnabled: true,
  mapIsSearchOnMoveControlVisible: false,
  mapSelectedFeatureId: 'none',
  mapShouldDisplayIconOnMap: true
};

export default class AreaMap extends React.Component {
  constructor(props) {
    super(props);
    this.onClickSearchOnMoveCallback = this.onClickSearchOnMoveCallback.bind(this);
  }

  componentDidMount() {
    // eslint-disable-next-line no-undef
    this.mapboxgl = mapboxgl;

    // eslint-disable-next-line no-undef
    this.mapboxgl.accessToken = this.props.mapAccessToken;

    // eslint-disable-next-line no-undef
    this.defaultPopup = new mapboxgl.Popup();

    const mapInitialCenter = [0, 0];
    if (this.props.mapCenter[0] && this.props.mapCenter[1]) {
      mapInitialCenter[0] = this.props.mapCenter[0];
      mapInitialCenter[1] = this.props.mapCenter[1];
    }

    // eslint-disable-next-line no-undef
    this.map = new mapboxgl.Map({
      container: this.props.mapContainer,
      style: this.props.mapStyle,
      center: mapInitialCenter,
      zoom: this.props.mapZoom
    });

    this.initMap();
  }

  componentWillReceiveProps(nextProps) {
    if (this.map === undefined) {
      return;
    }

    // show/hide controls on map
    this.updateMapControls(nextProps);

    // Check if we need to load new data on the map.
    let gdata;

    if (nextProps.mapGeoJsonData
        && (JSON.stringify(this.props.mapGeoJsonData) !== JSON.stringify(nextProps.mapGeoJsonData))) {
      gdata = nextProps.mapGeoJsonData;
    }
    else if (nextProps.locationData) {
      if (JSON.stringify(this.props.locationData) !== JSON.stringify(nextProps.locationData)) {
        gdata = getGeoJsonFromResultSet(nextProps.locationData);
      }
    }

    if (gdata) {
      const mapDataSource = this.map.getSource('recreationMapSource');
      if (mapDataSource) {
        mapDataSource.setData(gdata);

        if (gdata.features.length > 0) {
          // eslint-disable-next-line no-undef
          const bounds = new mapboxgl.LngLatBounds();
          gdata.features.forEach((feature) => {
            bounds.extend(feature.geometry.coordinates);
          });

          // maxzoom options ensures the map renders properly when only a single coord is set
          const options = {
            maxZoom: this.props.mapZoom,
            animate: false
          };

          this.map.fitBounds(bounds, options);
          this.map.zoomTo(this.props.mapZoom);
        }
      }
    }
    else if (JSON.stringify(this.props.mapCenter) !== JSON.stringify(nextProps.mapCenter) && nextProps.mapCenter[0] && nextProps.mapCenter[1]) {
      this.map.setCenter(nextProps.mapCenter);
    }

    if (nextProps.mapSelectedFeatureId && (this.props.mapSelectedFeatureId !== nextProps.mapSelectedFeatureId)) {
      this.placePopup(nextProps.mapSelectedFeatureId);
    }
  }

  shouldComponentUpdate() {
    return false;
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.remove();
    }
  }

  /**
   * Executes the callback function if one exists with the checked state of the checkbox.
   * @param {bool} e
   */
  onClickSearchOnMoveCallback(e) {
    if (this.props.onMapSearchOnMoveClick) {
      this.props.onMapSearchOnMoveClick(e);
    }
  }

  /**
   * Executes the onMapMoveEnd callback handler if was set by the container component.
   */
  onMapMoveEnd() {
    if (this.props.onMapMoveEnd) {
      this.props.onMapMoveEnd();
    }
  }

  /**
   * Exectues the onMapClick callback hander if any was set by the container component.
   * @param {object} e
   */
  onMapClick(e) {
    if (this.props.onMapClick) {
      this.props.onMapClick(e.features[0].properties.featureId);
    }
  }

  /**
   * Initializes Mapbox control
   */
  initMap() {
    // scale control
    // eslint-disable-next-line no-undef
    this.mapScaleControl = new this.mapboxgl.ScaleControl({
      maxWidth: 150,
      unit: 'imperial'
    });
    if (this.props.mapIsScaleControlVisible) {
      this.map.addControl(this.mapScaleControl);
    }

    this.mapSearchOnMoveControl = new MapSearchOnMoveControl(this.onClickSearchOnMoveCallback);
    if (this.props.mapIsSearchOnMoveControlVisible) {
      this.map.addControl(this.mapSearchOnMoveControl, 'top-left');
    }

    // zoom and pitch and north orientation
    // eslint-disable-next-line no-undef
    this.mapNavigationControl = new this.mapboxgl.NavigationControl();
    if (this.props.mapIsNavigationControlVisible) {
      this.map.addControl(this.mapNavigationControl, 'top-left');
    }

    if (!this.props.mapIsZoomByScrollEnabled) {
      this.map.scrollZoom.disable();
    }

    // full screen control
    this.mapFullscreenControl = new MapFullscreenControl();
    if (this.props.mapIsFullscreenControlVisible) {
      this.map.addControl(this.mapFullscreenControl);
    }

    this.mapLayerControl = new MapLayerControl();
    if (this.props.mapIsMapLayerControlVisible) {
      this.map.addControl(this.mapLayerControl);
    }

    // Add source and layers
    this.map.on('load', () => {
      let gdata;

      if (this.props.mapGeoJsonData) {
        gdata = this.props.mapGeoJsonData;
      }
      else {
        gdata = getGeoJsonFromResultSet(this.props.locationData);
      }

      if (gdata && gdata.features.length > 0) {
        // eslint-disable-next-line no-undef
        const bounds = new this.mapboxgl.LngLatBounds();

        gdata.features.forEach((feature) => {
          bounds.extend(feature.geometry.coordinates);
        });

        // maxzoom options ensures the map renders properly when only a single coord is set
        const options = {
          maxZoom: this.props.mapZoom,
          animate: false
        };

        this.map.fitBounds(bounds, options);
        this.map.zoomTo(this.props.mapZoom);
      }

      this.map.addSource('recreationMapSource', {
        type: 'geojson',
        data: gdata
      });

      this.map.addLayer({
        id: 'recreationMapLayer',
        type: 'symbol',
        source: 'recreationMapSource',
        layout: {
          'icon-image': '{icon}',
          'icon-allow-overlap': true,
          'text-allow-overlap': true,
          'text-field': '{label}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 11,
          'text-anchor': 'top',
          'text-offset': [0, 0.8],
          visibility: this.props.mapShouldDisplayIconOnMap ? 'visible' : 'none'
        }
      });

      this.map.addLayer({
        id: 'recreationCircleMapLayer',
        type: 'circle',
        source: 'recreationMapSource',
        layout: {
          visibility: !this.props.mapShouldDisplayIconOnMap ? 'visible' : 'none'
        },
        paint: {
          'circle-radius': 4,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#3d70b2',
          'circle-color': {
            property: 'category',
            type: 'categorical',
            default: '#ffffff',
            stops: [
              ['disabled', '#dcdcdc'],
              ['highlighted', '#996515']
            ]
          }
        }
      });

      this.map.addLayer({
        id: 'recreationCircleValueMapLayer',
        type: 'symbol',
        source: 'recreationMapSource',
        layout: {
          'text-field': '{label}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 11,
          'text-allow-overlap': true,
          'text-anchor': 'top',
          'text-offset': [0, 0.5],
          visibility: !this.props.mapShouldDisplayIconOnMap ? 'visible' : 'none'
        },
        paint: {
          'text-color': {
            property: 'category',
            type: 'categorical',
            default: '#3d70b2',
            stops: [
              ['disabled', '#000000'],
              ['highlighted', '#000000']
            ]
          }
        }
      });

      this.map.on('mouseenter', 'recreationMapLayer', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });

      this.map.on('mouseleave', 'recreationMapLayer', () => {
        this.map.getCanvas().style.cursor = '';
      });

      this.map.on('mouseenter', 'recreationCircleMapLayer', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });

      this.map.on('mouseleave', 'recreationCircleMapLayer', () => {
        this.map.getCanvas().style.cursor = '';
      });
    });

    this.map.on('click', 'recreationMapLayer', (e) => {
      if (this.defaultPopup) {
        this.defaultPopup.remove();
      }

      this.defaultPopup = new this.mapboxgl.Popup();
      this.defaultPopup
        .setLngLat(e.features[0].geometry.coordinates)
        .setHTML(e.features[0].properties.description)
        .addTo(this.map);

      this.onMapClick(e);
    });

    this.map.on('click', 'recreationCircleMapLayer', (e) => {
      if (this.defaultPopup) {
        this.defaultPopup.remove();
      }

      this.defaultPopup = new this.mapboxgl.Popup();
      this.defaultPopup
        .setLngLat(e.features[0].geometry.coordinates)
        .setHTML(e.features[0].properties.description)
        .addTo(this.map);

      this.onMapClick(e);
    });

    this.map.on('moveend', () => {
      this.onMapMoveEnd(this.map);
    });
  }

  /**
   * Displays a popup on the map using 'feturePointId' to locate the feature
   * in the GeoJSON properties object
   * ex: f.properties.featurePointId === featurePointId
   * @param {string} featureId
   */
  placePopup(featureId) {
    if (featureId === undefined || this.map === undefined || !this.map.loaded()) {
      return;
    }

    const features = this.map.queryRenderedFeatures({ layers: ['recreationMapLayer', 'recreationCircleMapLayer', 'recreationCircleValueMapLayer'] });

    if (features) {
      const feat = features.filter((featurePoint) => featurePoint.properties.featureId === featureId);

      if (feat.length > 0) {
        if (this.defaultPopup) {
          this.defaultPopup.remove();
        }

        this.defaultPopup = new this.mapboxgl.Popup();
        this.defaultPopup
          .setLngLat(feat[0].geometry.coordinates)
          .setHTML(feat[0].properties.description)
          .addTo(this.map);
      }
    }
  }

  /**
   * Add or remove Mapbox controls from the map.
   */
  updateMapControls(nextProps) {
    if (this.map === undefined || !this.map.loaded()) {
      return;
    }

    if (this.props.mapIsScaleControlVisible !== nextProps.mapIsScaleControlVisible) {
      if (nextProps.mapIsScaleControlVisible) {
        this.map.addControl(this.mapScaleControl);
      }
      else {
        this.map.removeControl(this.mapScaleControl);
      }
    }

    if (this.props.mapIsSearchOnMoveControlVisible !== nextProps.mapIsSearchOnMoveControlVisible) {
      if (nextProps.mapIsSearchOnMoveControlVisible) {
        this.map.addControl(this.mapSearchOnMoveControl, 'top-left');
      }
      else {
        this.map.removeControl(this.mapSearchOnMoveControl);
      }
    }

    if (this.props.mapIsNavigationControlVisible !== nextProps.mapIsNavigationControlVisible) {
      if (nextProps.mapIsNavigationControlVisible) {
        this.map.addControl(this.mapNavigationControl, 'top-left');
      }
      else {
        this.map.removeControl(this.mapNavigationControl);
      }
    }

    if (this.props.mapIsZoomByScrollEnabled !== nextProps.mapIsZoomByScrollEnabled) {
      if (nextProps.mapIsZoomByScrollEnabled) {
        this.map.scrollZoom.enable();
      }
      else {
        this.map.scrollZoom.disable();
      }
    }

    if (this.props.mapIsFullscreenControlVisible !== nextProps.mapIsFullscreenControlVisible) {
      if (nextProps.mapIsFullscreenControlVisible) {
        this.map.addControl(this.mapFullscreenControl);
      }
      else {
        this.map.removeControl(this.mapFullscreenControl);
      }
    }

    if (this.props.mapIsMapLayerControlVisible !== nextProps.mapIsMapLayerControlVisible) {
      if (nextProps.mapIsMapLayerControlVisible) {
        this.map.addControl(this.mapLayerControl);
      }
      else {
        this.map.removeControl(this.mapLayerControl);
      }
    }
  }

  render() {
    return (
      null
    );
  }
}

AreaMap.propTypes = propTypes;
AreaMap.defaultProps = defaultProps;
